import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Send } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';
import html2pdf from 'html2pdf.js';
import { toast } from 'sonner';

export default function SalesReceiptViewer({ sale, open, onClose }) {
  const [companySettings, setCompanySettings] = useState(null);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [includeReviewLink, setIncludeReviewLink] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (open) {
      fetchCompanySettings();
    }
  }, [open]);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/company`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to load company settings');
    }
  };

  const handleDownload = () => {
    const element = printRef.current;
    const opt = {
      margin: 0.5,
      filename: `receipt-${sale.id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  const handleSend = async (platform) => {
    setShowSendOptions(false);
    toast.info('Generating PDF...');
    
    const element = printRef.current;
    const opt = {
      margin: 0.5,
      filename: `receipt-${sale.id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };

    try {
      // Generate PDF as blob
      const pdfBlob = await html2pdf().set(opt).from(element).output('blob');
      const file = new File([pdfBlob], `receipt-${sale.id}.pdf`, { type: 'application/pdf' });

      // Get phone number
      const storePhone = companySettings?.store_handler_phone || '';
      const phoneNumber = storePhone.replace(/[^0-9]/g, '');

      // Build message with optional review links
      let messageText = `Receipt for ${sale.customer_name} - ${formatCurrency(sale.total)}`;
      
      if (includeReviewLink && sale.items && sale.items.length > 0) {
        messageText += '\n\n--- Leave a Review ---\nThank you for your purchase! We would love to hear your feedback:\n';
        sale.items.forEach(item => {
          const reviewUrl = `${window.location.origin}/review/write?productId=${item.product_id}&saleId=${sale.id}`;
          messageText += `\n${item.product_name}:\n${reviewUrl}\n`;
        });
      }

      // Try Web Share API first (works on mobile)
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Sales Receipt',
          text: messageText,
          files: [file]
        });
        toast.success('Shared successfully!');
      } else {
        // Fallback: Download PDF and show instructions
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${sale.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Use the same message for fallback
        const fullMessage = `Sales Receipt\nReceipt #: ${sale.id.substring(0, 8)}\n${messageText}`;
        
        if (platform === 'whatsapp') {
          toast.success('PDF downloaded! Opening WhatsApp...');
          setTimeout(() => {
            window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(fullMessage + '\n\nPlease attach the downloaded PDF file.')}`, '_blank');
          }, 500);
        } else if (platform === 'viber') {
          const formattedPhone = storePhone.startsWith('+') ? storePhone : `+${phoneNumber}`;
          toast.success('PDF downloaded! Opening Viber...');
          if (navigator.clipboard) {
            navigator.clipboard.writeText(fullMessage + '\n\nPlease attach the downloaded PDF file.');
          }
          setTimeout(() => {
            window.open(`viber://chat?number=${encodeURIComponent(formattedPhone)}`, '_blank');
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share. Please try downloading instead.');
    }
  };

  if (!sale) return null;

  const saleDate = new Date(sale.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Sales Receipt Preview</DialogTitle>
              <div className="flex gap-2">
                <Button onClick={handleDownload} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button onClick={() => setShowSendOptions(true)} size="sm" variant="outline">
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div ref={printRef} id="receipt-document" className="bg-white p-8 print:p-0">
            {/* Header */}
            <div className="text-center mb-8 border-b-2 border-slate-300 pb-6">
              {companySettings?.company_logo ? (
                <div className="flex justify-center mb-4">
                  <img src={companySettings.company_logo} alt="Company Logo" className="h-20" />
                </div>
              ) : null}
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {companySettings?.company_name || 'AN-NISA Nexus'}
              </h1>
              <h2 className="text-xl font-semibold text-emerald-600">SALES RECEIPT</h2>
            </div>

            {/* Receipt Details */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div>
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-semibold">Receipt #:</span> {sale.sale_number}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Date:</span> {saleDate}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-semibold">Cashier:</span> {sale.created_by}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Payment:</span> {sale.payment_method.toUpperCase()}
                </p>
              </div>
            </div>

            {/* Customer Info */}
            {sale.customer_name && (
              <div className="mb-8 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-700">Customer: {sale.customer_name}</p>
              </div>
            )}

            {/* Items Table */}
            <table className="w-full mb-8">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 text-sm font-semibold text-slate-700">ITEM</th>
                  <th className="text-center py-3 text-sm font-semibold text-slate-700">QTY</th>
                  <th className="text-right py-3 text-sm font-semibold text-slate-700">PRICE</th>
                  <th className="text-right py-3 text-sm font-semibold text-slate-700">DISCOUNT</th>
                  <th className="text-right py-3 text-sm font-semibold text-slate-700">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {sale.items.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-200">
                    <td className="py-3 text-slate-900 font-medium">{item.product_name}</td>
                    <td className="py-3 text-center text-slate-700">{item.quantity}</td>
                    <td className="py-3 text-right text-slate-700">{formatCurrency(item.price)}</td>
                    <td className="py-3 text-right text-slate-700">
                      {item.discount > 0 ? `-${formatCurrency(item.discount)}` : '-'}
                    </td>
                    <td className="py-3 text-right font-medium text-slate-900">{formatCurrency(item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-64">
                <div className="flex justify-between py-2 text-slate-700">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(sale.subtotal)}</span>
                </div>
                {sale.discount > 0 && (
                  <div className="flex justify-between py-2 text-slate-700">
                    <span>Discount:</span>
                    <span className="text-red-600">-{formatCurrency(sale.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 text-slate-700">
                  <span>Tax:</span>
                  <span>{formatCurrency(sale.tax)}</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 border-slate-300 text-xl font-bold text-slate-900">
                  <span>TOTAL PAID:</span>
                  <span className="text-emerald-600">{formatCurrency(sale.total)}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {sale.notes && (
              <div className="mb-8 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">{sale.notes}</p>
              </div>
            )}

            {/* Transfer Slip */}
            {sale.transfer_slip && sale.payment_method === 'bank_transfer' && (
              <div className="mb-8">
                <p className="text-sm font-semibold text-slate-700 mb-2">Transfer Slip:</p>
                <img 
                  src={sale.transfer_slip} 
                  alt="Transfer Slip" 
                  className="w-full max-w-md mx-auto border-2 border-slate-300 rounded-lg"
                />
              </div>
            )}

            {/* Payment Confirmation */}
            <div className="text-center mb-8 p-4 bg-emerald-50 border-2 border-emerald-600 rounded-lg">
              <p className="text-lg font-bold text-emerald-800">✓ PAYMENT RECEIVED</p>
              <p className="text-sm text-emerald-700 mt-1">Thank you for your purchase!</p>
            </div>

            {/* Footer */}
            <div className="text-center text-sm text-slate-500 border-t border-slate-200 pt-4">
              <p>This is a computer-generated receipt</p>
              <p className="mt-2 font-semibold">{companySettings?.company_name || 'AN-NISA Nexus'}</p>
              <p className="mt-4 text-xs">Powered by AN-NISA Nexus Business Management System</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #receipt-document,
          #receipt-document * {
            visibility: visible;
          }
          #receipt-document {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          button {
            display: none !important;
          }
        }
      `}</style>

      {/* Send Options Dialog */}
      <Dialog open={showSendOptions} onOpenChange={setShowSendOptions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Receipt</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            <p className="text-slate-600 text-sm">
              PDF will be generated and shared via your chosen platform:
            </p>
            
            {/* Include Review Link Checkbox */}
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="include-review-link"
                checked={includeReviewLink}
                onChange={(e) => setIncludeReviewLink(e.target.checked)}
                className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
              />
              <label htmlFor="include-review-link" className="text-sm text-slate-700 cursor-pointer">
                Include review links in message
              </label>
            </div>
            
            <Button
              onClick={() => handleSend('whatsapp')}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white font-semibold"
            >
              <Send className="w-5 h-5 mr-2" />
              Send PDF via WhatsApp
            </Button>
            
            <Button
              onClick={() => handleSend('viber')}
              className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
            >
              <Send className="w-5 h-5 mr-2" />
              Send PDF via Viber
            </Button>
            
            <p className="text-xs text-slate-500">
              💡 PDF will be downloaded. On mobile, you can share it directly. On desktop, attach the downloaded file manually.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
